import React, { useMemo, useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { TaskCard } from "../../components/shared/TaskCard";
import { EmptyState, LoadingState, ErrorState } from "../../components/shared/EmptyState";
import { studentNav } from "../../components/shared/navConfig";
import { useApi, fmtDate } from "../../lib/useApi";

const uniq = (a) => [...new Set(a.filter(Boolean))].sort();
const sel = "w-full text-sm px-3 py-2.5 rounded-md border outline-none";
const selStyle = { borderColor: "var(--border-strong)", background: "var(--surface)", color: "var(--text)" };

const Filter = ({ label, children }) => (
  <div className="mb-4"><div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: "var(--text-dim)" }}>{label}</div>{children}</div>
);

export default function FindTasks() {
  const { data, loading, error, reload } = useApi("/tasks/live");
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [mode, setMode] = useState("");
  const [skill, setSkill] = useState("");
  const [minBudget, setMinBudget] = useState("");
  const [drawer, setDrawer] = useState(false);

  const tasks = useMemo(() => (data || []).map(t => ({
    id: t.task_id, title: t.title, company: t.company_name, category: t.category,
    location: t.address || t.city || "—", city: t.city, distance: t.work_mode === "Remote" ? "Remote" : "",
    skills: t.skills || [], budget: Number(t.budget) || 0, deadline: t.deadline || "—",
    workMode: t.work_mode || "On-site", status: "Open", posted: fmtDate(t.created_at),
  })), [data]);
  const cities = uniq(tasks.map(t => t.city));
  const modes = uniq(tasks.map(t => t.workMode));
  const skills = uniq(tasks.flatMap(t => t.skills));

  const filtered = tasks.filter(t => {
    const hay = `${t.title} ${t.company} ${t.skills.join(" ")}`.toLowerCase();
    if (q && !hay.includes(q.toLowerCase())) return false;
    if (city && t.city !== city) return false;
    if (mode && t.workMode !== mode) return false;
    if (skill && !t.skills.includes(skill)) return false;
    if (minBudget && t.budget < Number(minBudget)) return false;
    return true;
  });

  const filters = (
    <>
      <Filter label="City"><select data-testid="filter-city" value={city} onChange={e => setCity(e.target.value)} className={sel} style={selStyle}><option value="">All</option>{cities.map(c => <option key={c}>{c}</option>)}</select></Filter>
      <Filter label="Work mode"><select data-testid="filter-mode" value={mode} onChange={e => setMode(e.target.value)} className={sel} style={selStyle}><option value="">All</option>{modes.map(c => <option key={c}>{c}</option>)}</select></Filter>
      <Filter label="Skill"><select data-testid="filter-skill" value={skill} onChange={e => setSkill(e.target.value)} className={sel} style={selStyle}><option value="">All</option>{skills.map(c => <option key={c}>{c}</option>)}</select></Filter>
      <Filter label="Min budget (₹)"><input data-testid="filter-budget" type="number" inputMode="numeric" value={minBudget} onChange={e => setMinBudget(e.target.value)} className={sel} style={selStyle}/></Filter>
      <button data-testid="clear-filters-btn" onClick={() => { setCity(""); setMode(""); setSkill(""); setMinBudget(""); }} className="w-full text-xs py-2" style={{ color: "var(--text-dim)" }}>Clear all filters</button>
    </>
  );

  return (
    <DashboardShell nav={studentNav} title="Find tasks" subtitle={data ? `${filtered.length} live tasks` : "Live tasks approved by admin"}>
      <div className="sticky top-[68px] z-20 py-2 mb-4 flex gap-2" style={{ background: "var(--bg)" }}>
        <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-full border min-w-0" style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
          <Search size={14} style={{ color: "var(--text-dim)" }}/>
          <input data-testid="tasks-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by title, company, skill…" className="flex-1 min-w-0 bg-transparent outline-none text-sm" style={{ color: "var(--text)" }}/>
        </div>
        <button data-testid="tasks-filters-btn" onClick={() => setDrawer(true)} className="lg:hidden btn-ghost px-4 rounded-full"><SlidersHorizontal size={15}/></button>
      </div>
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawer(false)}/>
          <div data-testid="tasks-filters-drawer" className="absolute inset-y-0 right-0 w-[86%] max-w-sm p-5 overflow-y-auto" style={{ background: "var(--surface)" }}>
            <div className="flex items-center justify-between mb-4"><span className="font-display" style={{ color: "var(--text)" }}>Filters</span><button data-testid="tasks-drawer-close" onClick={() => setDrawer(false)} style={{ color: "var(--text-muted)" }}><X size={18}/></button></div>
            {filters}
            <button data-testid="tasks-drawer-apply" onClick={() => setDrawer(false)} className="btn-primary w-full mt-3 py-3 rounded-full text-sm">Show {filtered.length} tasks</button>
          </div>
        </div>
      )}
      <div className="grid lg:grid-cols-4 gap-6">
        <aside className="hidden lg:block lg:col-span-1 card-surface p-5 h-fit sticky top-36">{filters}</aside>
        <div className="lg:col-span-3 grid md:grid-cols-2 gap-4 content-start">
          {loading ? <div className="md:col-span-2"><LoadingState/></div> : error ? <div className="md:col-span-2"><ErrorState message={error} onRetry={reload}/></div> : filtered.length === 0 ? (
            <div className="md:col-span-2 card-surface"><EmptyState icon={Search} title={tasks.length ? "No tasks match your filters" : "No live tasks right now"} desc={tasks.length ? "Try clearing a filter." : "New tasks show up here as soon as admin approves them."} testId="tasks-empty"/></div>
          ) : filtered.map(t => <TaskCard key={t.id} task={t}/>)}
        </div>
      </div>
    </DashboardShell>
  );
}
